<template>
    <div class="p-4 h-full flex flex-col gap-2">
        <div v-if="documentMeta">
            Sist generert {{ formatDateTime(documentMeta.createdAt) }}
        </div>
        <div v-else>
            Ingen data generert enda. Trykk på generer data.
        </div>
        <div class="z-0 flex gap-2">
            <UButton @click="handleClickGenerate">Generer data</UButton>
            <div v-if="gridApi">
                Oppsett:
                <UPopover>
                    <UButton label="Lagre preset"/>
                    <template #content>
                        <UInput v-model="newPresetName"/>
                        <UButton @click="savePreset(gridApi)">Save</UButton>
                    </template>
                </UPopover>
                <UPopover>
                    <UButton label="Gjenopprett preset"/>
                    <template #content>
                        <ul>
                            <li v-for="key in Object.keys(presets)" :key="key" class="flex p-1 cursor-pointer items-center gap-1">
                                <div @click="restorePreset(gridApi, key)">{{ key }}</div>
                                <UButton
                                    variant="icon"
                                    icon="i-ph-trash"
                                    color="error"
                                    @click="deletePreset(key)"
                                />
                            </li>
                        </ul>
                    </template>
                </UPopover>
            </div>
        </div>

        <ag-grid-vue
            class="h-full grow z-0"
            :column-defs="columnDefs"
            :grid-options="gridOptions"
            row-model-type="serverSide"
            :theme="agTheme"
            :server-side-datasource="serverSideDataSource"
        />
    </div>
</template>

<script setup lang="ts">
import './duck.service'
import {AgGridVue} from 'ag-grid-vue3'
import {Column, useDuckDB} from '@/pracsys-bi/duck.service'
import {computed, onMounted, onUnmounted, shallowRef} from 'vue'
import {ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IServerSideDatasource, IServerSideGetRowsParams,} from 'ag-grid-community'
import {agTheme} from '@/agUtils/ag-theme.service'
import {deletePreset, newPresetName, presets, restorePreset, savePreset} from '@/pracsys-bi/preset.service'
import {formatCurrency, formatDateTime, formattedDate} from '@/services/formatter.service'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'

const gridApi = shallowRef<GridApi | undefined>()

const {onEvents, cleanUp} = useEmitter()
const {columns, newSnapshot, query, clear, documentMeta, init} = useDuckDB()

const serverSideDataSource = computed<IServerSideDatasource>(() => {
    return {
        getRows(params: IServerSideGetRowsParams<any>): void {
            if (!documentMeta.value) {
                params.success({rowData: []})
                return
            }

            query(params.request)
                .then(s => {
                    params.success({rowData: s})
                })
                .catch(() => params.fail())

        }
    }
})

const gridOptions: GridOptions = {
    enableCharts: true,
    sideBar: ['columns', 'filters-new'],
    cellSelection: true,
    enableFilterHandlers: true,
    autoGroupColumnDef: {
        minWidth: 300
    },
    defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enableRowGroup: true,
        enablePivot: true,
        filter: true
    },
    onGridReady(event: GridReadyEvent) {
        gridApi.value = event.api
    }
}

const columnDefs = computed<ColDef[]>(() => {
    if (!columns.value) return []

    return columns.value.map(col => ({
        field: col.column_name,
        valueFormatter: (params: ICellRendererParams) => cellRenderer(params, col)
    })) as ColDef[]
})

const cellRenderer = (params: ICellRendererParams, col: Column) => {
    switch (col.column_type) {
        case 'DATE':
            return formattedDate(params.value)
        case 'BOOLEAN':
            return params.value ? 'Ja' : 'Nei'
        case 'TIMESTAMP WITH TIME ZONE':
            return formatDateTime(params.value)
        case 'VARCHAR[]':
            return params.value.length > 0 ? (params.value as string[]).join(', ') : ''
        case 'BIGINT':
        case 'INTEGER':
            return col.column_name.includes('amount') ? formatCurrency(Number(params.value) / 100) : Number(params.value)
        case 'DECIMAL(5,4)':
            return Number(params.value).toFixed(4)
        default:
            return params.value
    }
}

const initGrid = async () => {
    await init()
    gridApi.value?.refreshServerSide()
}

const handleClickGenerate = async () => {
    await newSnapshot()
    gridApi.value?.refreshServerSide()
}

onMounted(async () => {
    await initGrid()
    onEvents([ORG_SELECTED], initGrid)
    onEvents([NO_ORG_SELECTED, LOGOUT], async () => {
        await clear()
        gridApi.value?.refreshServerSide()
    })
})

onUnmounted(cleanUp)

</script>

<style scoped>

</style>